// frontend/app/screens/DavinciCertWallScreen.js
//
// BDD 2026-06-14 — Da Vinci 3-Day Challenge Certificate Wall (public display).
//
// A full-screen, auto-scrolling display of ACTIVATED (redeemed) certificates.
// Separate from the scanner: the scanner redeems + prints one cert at a time;
// this wall is the celebratory display that cycles through everyone who has
// earned theirs, with the newest activation popping in immediately.
//
// No PDFs are stored — each slide is rendered LIVE from a DB row via the shared
// buildCertHtml template (true WYSIWYG with the printed certificate). The 3D
// subject models are snapshotted ONCE per page load (prewarmAllModels) and
// cached, so building each slide's HTML is instant.
//
// Web/kiosk only (needs WebGL + model-viewer + Google Fonts). Gated to the same
// kiosk/admin roles as the scanner (also enforced server-side on the API).

import React, { useCallback, useEffect, useRef, useState } from "react";
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet, Platform } from "react-native";

import { useAuth } from "../AuthContext";
import { FASTAPI_BASE_URL } from "../config";
import {
  DAVINCI_LOGO,
  SARAS_LOGO,
  buildCertHtml,
  formatDate,
  resolveUri,
} from "../utils/davinciCertHtml";
import { prewarmAllModels, getModelImgsForSubjects } from "../utils/davinciModels";

const ROOT_ID = "dv-wall-root";
const STYLE_ID = "dv-wall-style";
const FONTS_ID = "dv-wall-fonts";

// Persisted carousel speed.
const INTERVAL_KEY = "dv_wall_interval_ms";
const DEFAULT_INTERVAL_MS = 8000;
const MIN_INTERVAL_MS = 3000;
const MAX_INTERVAL_MS = 20000;
const PRESETS_MS = [5000, 8000, 12000, 15000];

// Poll for newly-activated certs this often.
const POLL_MS = 5000;
// "Just earned!" flash duration after a pop-in.
const FLASH_MS = 3000;
// Cross-fade duration (kept in sync with the CSS transition below).
const FADE_MS = 700;

const FONTS_HREF =
  "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Jost:wght@400;500;600&display=swap";

// Kiosk login + any admin/moderator role may view the wall. Internal admins
// always can. Mirrors backend ALLOWED_SCAN_ROLES in davinci_routes.py and the
// scanner's ALLOWED_SCAN_ROLES set.
const ALLOWED_SCAN_ROLES = new Set([
  "davinci_kiosk",
  "moderator",
  "partneradmin",
  "admin",
  "institute_admin",
  "schooladmin",
  "superadmin",
]);

const isKioskUser = (user) =>
  ALLOWED_SCAN_ROLES.has((user?.role || "").toLowerCase()) ||
  user?.is_internal_admin === true;

const isWeb = () =>
  Platform.OS === "web" && typeof window !== "undefined" && typeof document !== "undefined";

// One-time injection: fonts, navy gradient background, the "Just earned!" flash
// keyframes, and the cross-fading iframe layer styling. Self-contained (own id).
const injectWallTheme = () => {
  if (typeof document === "undefined") return;
  if (!document.getElementById(FONTS_ID)) {
    const l = document.createElement("link");
    l.id = FONTS_ID;
    l.rel = "stylesheet";
    l.href = FONTS_HREF;
    document.head.appendChild(l);
  }
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
    #${ROOT_ID} {
      background:
        radial-gradient(60% 40% at 50% -10%, #18367a 0%, rgba(24,54,122,0) 60%),
        radial-gradient(70% 55% at 50% 120%, #142a63 0%, rgba(20,42,99,0) 55%),
        linear-gradient(160deg,#060f2c 0%,#0b1f4d 50%,#060f2c 100%) !important;
    }
    .dv-wall-stage { position:relative; width:100%; height:100%; }
    .dv-wall-frame {
      position:absolute; inset:0; opacity:0; transition: opacity ${FADE_MS}ms ease-in-out;
      display:flex; align-items:center; justify-content:center;
    }
    .dv-wall-frame.show { opacity:1; }
    .dv-wall-iframe {
      width:100%; height:100%; border:0; display:block; background:transparent;
    }
    @keyframes dvWallFlash {
      0%   { transform:translate(-50%,8px) scale(.9); opacity:0; }
      12%  { transform:translate(-50%,0) scale(1); opacity:1; }
      80%  { opacity:1; }
      100% { transform:translate(-50%,-6px) scale(.98); opacity:0; }
    }
    .dv-wall-flash { animation: dvWallFlash ${FLASH_MS}ms ease-in-out forwards; }
    @keyframes dvWallPulse { 0%,100%{ opacity:.55 } 50%{ opacity:1 } }
    .dv-wall-pulse { animation: dvWallPulse 2.2s ease-in-out infinite; }
  `;
  document.head.appendChild(s);
};

// --- localStorage helpers (guarded) -----------------------------------------
const readStoredInterval = () => {
  if (typeof window === "undefined") return DEFAULT_INTERVAL_MS;
  try {
    const raw = window.localStorage?.getItem(INTERVAL_KEY);
    const n = raw ? parseInt(raw, 10) : NaN;
    if (Number.isFinite(n) && n >= MIN_INTERVAL_MS && n <= MAX_INTERVAL_MS) return n;
  } catch {
    /* localStorage unavailable (private mode / SSR) */
  }
  return DEFAULT_INTERVAL_MS;
};

const writeStoredInterval = (ms) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage?.setItem(INTERVAL_KEY, String(ms));
  } catch {
    /* ignore */
  }
};

// Build the print/preview HTML for a single completion row (cache hits after
// prewarm make this fast). Returns a slide { code, name, html, redeemed_at }.
const buildSlide = async (cert) => {
  const subjects = Array.isArray(cert?.subjects) ? cert.subjects.filter(Boolean) : [];
  const modelImgs = await getModelImgsForSubjects(subjects);
  const html = buildCertHtml({
    studentName: cert?.student_name || "Student",
    dateStr: formatDate(cert?.completed_at),
    code: cert?.redeem_code || "",
    davinciUri: resolveUri(DAVINCI_LOGO),
    sarasUri: resolveUri(SARAS_LOGO),
    modelImgs,
    autoPrint: false,
  });
  return {
    code: cert?.redeem_code || "",
    name: cert?.student_name || "Student",
    html,
    redeemed_at: cert?.redeemed_at || null,
  };
};

function DavinciCertWallInner() {
  const { user, fetchWithAuth } = useAuth();
  const authorized = isKioskUser(user);
  const web = isWeb();

  const [phase, setPhase] = useState("loading"); // "loading" | "ready" | "error"
  const [slides, setSlides] = useState([]); // [{ code, name, html, redeemed_at }]
  const [activeIdx, setActiveIdx] = useState(0);
  const [justEarned, setJustEarned] = useState(false);
  const [intervalMs, setIntervalMs] = useState(readStoredInterval);
  const [showControls, setShowControls] = useState(true);

  // Refs kept in sync so interval/poll callbacks read fresh values without
  // re-subscribing every render.
  const slidesRef = useRef([]);
  const codesRef = useRef(new Set()); // de-dupe by redeem_code
  const latestSinceRef = useRef(null); // server_time high-water mark for delta poll
  const flashTimerRef = useRef(null);
  const advanceTimerRef = useRef(null);
  const pollTimerRef = useRef(null);

  useEffect(() => {
    slidesRef.current = slides;
  }, [slides]);

  // Inject theme once (web).
  useEffect(() => {
    if (web) injectWallTheme();
  }, [web]);

  // De-dupe + append slides; returns the count actually added.
  const appendSlides = useCallback((built, { prepend } = {}) => {
    const fresh = built.filter((s) => s && s.code && !codesRef.current.has(s.code));
    if (!fresh.length) return 0;
    fresh.forEach((s) => codesRef.current.add(s.code));
    setSlides((prev) => (prepend ? [...fresh, ...prev] : [...prev, ...fresh]));
    return fresh.length;
  }, []);

  // --- Startup: prewarm models, fetch the full list, build every slide ------
  useEffect(() => {
    if (!web || !authorized) return;
    let cancelled = false;

    (async () => {
      try {
        await prewarmAllModels();
        if (cancelled) return;

        const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/davinci/certificates?limit=60`);
        if (res.status === 403) {
          if (!cancelled) setPhase("error");
          return;
        }
        if (!res.ok) throw new Error(`List failed (${res.status})`);
        const data = await res.json();
        if (cancelled) return;

        latestSinceRef.current = data?.server_time || new Date().toISOString();

        const certs = Array.isArray(data?.certificates) ? data.certificates : [];
        const built = [];
        for (const cert of certs) {
          // Sequential: getModelImgsForSubjects shares the WebGL renderer mutex.
          // Cache hits after prewarm make each iteration cheap.
          // eslint-disable-next-line no-await-in-loop
          const slide = await buildSlide(cert);
          if (cancelled) return;
          built.push(slide);
        }
        appendSlides(built);
        if (!cancelled) setPhase("ready");
      } catch (e) {
        console.error("[DavinciCertWall] startup failed:", e);
        if (!cancelled) setPhase("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [web, authorized, fetchWithAuth, appendSlides]);

  // --- Auto-advance carousel ------------------------------------------------
  useEffect(() => {
    if (phase !== "ready") return undefined;
    if (advanceTimerRef.current) clearInterval(advanceTimerRef.current);
    advanceTimerRef.current = setInterval(() => {
      const n = slidesRef.current.length;
      if (n <= 1) return; // single cert: just hold it
      setActiveIdx((i) => (i + 1) % n);
    }, intervalMs);
    return () => {
      if (advanceTimerRef.current) clearInterval(advanceTimerRef.current);
      advanceTimerRef.current = null;
    };
  }, [phase, intervalMs]);

  // --- Pop-in newest: delta poll every POLL_MS -----------------------------
  useEffect(() => {
    if (phase !== "ready" || !web || !authorized) return undefined;

    const poll = async () => {
      const since = latestSinceRef.current;
      if (!since) return;
      try {
        const res = await fetchWithAuth(
          `${FASTAPI_BASE_URL}/davinci/certificates?since=${encodeURIComponent(since)}`
        );
        if (!res.ok) return; // swallow; retry next tick
        const data = await res.json();
        const certs = Array.isArray(data?.certificates) ? data.certificates : [];
        // Advance the high-water mark regardless, so we don't refetch the same window.
        if (data?.server_time) latestSinceRef.current = data.server_time;

        if (!certs.length) return;

        // Newest first so prepending keeps the freshest at index 0.
        const sorted = [...certs].sort((a, b) => {
          const ta = a?.redeemed_at ? Date.parse(a.redeemed_at) : 0;
          const tb = b?.redeemed_at ? Date.parse(b.redeemed_at) : 0;
          return tb - ta;
        });

        const built = [];
        for (const cert of sorted) {
          if (codesRef.current.has(cert?.redeem_code)) continue;
          // eslint-disable-next-line no-await-in-loop
          built.push(await buildSlide(cert));
        }
        const added = appendSlides(built, { prepend: true });
        if (added > 0) {
          // Show the newest NOW and flash the "Just earned!" badge.
          setActiveIdx(0);
          setJustEarned(true);
          if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
          flashTimerRef.current = setTimeout(() => setJustEarned(false), FLASH_MS);
        }
      } catch (e) {
        console.error("[DavinciCertWall] poll error (will retry):", e);
      }
    };

    pollTimerRef.current = setInterval(poll, POLL_MS);
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };
  }, [phase, web, authorized, fetchWithAuth, appendSlides]);

  // Clear the flash timer on unmount.
  useEffect(
    () => () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    },
    []
  );

  const setSpeed = useCallback((ms) => {
    const clamped = Math.max(MIN_INTERVAL_MS, Math.min(MAX_INTERVAL_MS, Math.round(ms)));
    setIntervalMs(clamped);
    writeStoredInterval(clamped);
  }, []);

  // --- Guards ---------------------------------------------------------------
  if (!web) {
    return (
      <View nativeID={ROOT_ID} style={styles.centered}>
        <Text style={styles.guardTitle}>Display available on the web kiosk only</Text>
        <Text style={styles.guardBody}>
          Open the Certificate Wall in the browser display to see live certificates.
        </Text>
      </View>
    );
  }

  if (!authorized) {
    return (
      <View nativeID={ROOT_ID} style={styles.centered}>
        <Text style={styles.guardTitle}>Da Vinci display</Text>
        <Text style={styles.guardBody}>
          Moderator or admin login required to run the certificate display.
        </Text>
      </View>
    );
  }

  if (phase === "error") {
    return (
      <View nativeID={ROOT_ID} style={styles.centered}>
        <Text style={styles.guardTitle}>Couldn't load the display</Text>
        <Text style={styles.guardBody}>
          The certificate list could not be loaded. Confirm this device is signed in as a
          kiosk or admin account, then refresh.
        </Text>
      </View>
    );
  }

  if (phase === "loading") {
    return (
      <View nativeID={ROOT_ID} style={styles.centered}>
        <Text style={styles.eyebrow}>3-DAY CHALLENGE</Text>
        <Text style={styles.loadingTitle}>Preparing display…</Text>
        <ActivityIndicator size="large" color="#e7c46a" style={{ marginTop: 18 }} />
        <Text style={styles.guardBody}>Rendering 3D models and loading certificates.</Text>
      </View>
    );
  }

  // phase === "ready"
  const hasSlides = slides.length > 0;

  return (
    <View nativeID={ROOT_ID} style={styles.fill}>
      {!hasSlides ? (
        <View style={styles.centered}>
          <Text style={styles.eyebrow}>3-DAY CHALLENGE</Text>
          <Text style={styles.waitTitle}>Waiting for the first certificate…</Text>
          <Text style={styles.guardBody}>
            Activated certificates appear here automatically — the newest pops in first.
          </Text>
        </View>
      ) : (
        <CertCarousel slides={slides} activeIdx={activeIdx} />
      )}

      {/* "Just earned!" flash badge */}
      {justEarned && (
        <div className="dv-wall-flash" style={WEB_FLASH_STYLE}>
          ✦ Just earned!
        </div>
      )}

      {/* Speed control — hideable, pinned top-right */}
      <SpeedControl
        visible={showControls}
        intervalMs={intervalMs}
        onSetSpeed={setSpeed}
        onToggle={() => setShowControls((v) => !v)}
      />
    </View>
  );
}

// Two stacked iframes that cross-fade. Each slide gets a keyed <div> so React
// keeps the iframe mounted (no reload) and only toggles the .show opacity class.
function CertCarousel({ slides, activeIdx }) {
  return (
    <div className="dv-wall-stage">
      {slides.map((s, i) => (
        <div
          key={s.code || i}
          className={`dv-wall-frame${i === activeIdx ? " show" : ""}`}
          aria-hidden={i === activeIdx ? "false" : "true"}
        >
          <iframe className="dv-wall-iframe" title={`Certificate — ${s.name}`} srcDoc={s.html} />
        </div>
      ))}
    </div>
  );
}

// Web-only speed control. Presets are the priority; the range slider is a bonus.
function SpeedControl({ visible, intervalMs, onSetSpeed, onToggle }) {
  if (!visible) {
    return (
      <button
        type="button"
        onClick={onToggle}
        style={SC_TOGGLE_HIDDEN_STYLE}
        title="Show display controls"
      >
        ⚙
      </button>
    );
  }
  return (
    <div style={SC_PANEL_STYLE}>
      <div style={SC_HEADER_STYLE}>
        <span style={SC_LABEL_STYLE}>SPEED</span>
        <button type="button" onClick={onToggle} style={SC_HIDE_BTN_STYLE} title="Hide controls">
          ✕
        </button>
      </div>
      <div style={SC_PRESETS_ROW_STYLE}>
        {PRESETS_MS.map((ms) => {
          const active = ms === intervalMs;
          return (
            <button
              key={ms}
              type="button"
              onClick={() => onSetSpeed(ms)}
              style={active ? SC_PRESET_ACTIVE_STYLE : SC_PRESET_STYLE}
            >
              {Math.round(ms / 1000)}s
            </button>
          );
        })}
      </div>
      <input
        type="range"
        min={MIN_INTERVAL_MS}
        max={MAX_INTERVAL_MS}
        step={500}
        value={intervalMs}
        onChange={(e) => onSetSpeed(Number(e.target.value))}
        style={SC_RANGE_STYLE}
      />
      <div style={SC_VALUE_STYLE}>{(intervalMs / 1000).toFixed(1)}s per slide</div>
    </div>
  );
}

// Keep any render/network error from white-screening the public display.
class WallErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error("[DavinciCertWall] error boundary caught:", error, info);
  }
  reset = () => this.setState({ hasError: false });
  render() {
    if (this.state.hasError) {
      return (
        <View nativeID={ROOT_ID} style={styles.centered}>
          <Text style={styles.guardTitle}>Something went wrong</Text>
          <Text style={styles.guardBody}>
            The display hit an error. Tap below to reset and try again.
          </Text>
          <TouchableOpacity style={[styles.btn, styles.btnGold, { marginTop: 16 }]} onPress={this.reset}>
            <Text style={styles.btnGoldText}>Reset display</Text>
          </TouchableOpacity>
        </View>
      );
    }
    return this.props.children;
  }
}

export default function DavinciCertWallScreen() {
  return (
    <WallErrorBoundary>
      <DavinciCertWallInner />
    </WallErrorBoundary>
  );
}

const FONT_DISPLAY = '"Cormorant Garamond", Georgia, serif';
const FONT_LABEL = '"Jost", system-ui, sans-serif';
const GOLD = "#e7c46a";

// Plain-object styles for the web-only DOM elements (iframes, control panel,
// flash badge). RN StyleSheet objects aren't valid inline DOM styles, so these
// stay as CSS-property objects.
const WEB_FLASH_STYLE = {
  position: "fixed",
  top: "26px",
  left: "50%",
  zIndex: 50,
  padding: "10px 26px",
  borderRadius: "999px",
  background: "linear-gradient(180deg,#f3d98a,#e7c46a)",
  color: "#0b2a63",
  fontFamily: FONT_LABEL,
  fontWeight: 800,
  fontSize: "16px",
  letterSpacing: "2px",
  textTransform: "uppercase",
  boxShadow: "0 10px 30px rgba(231,196,106,0.5), 0 2px 0 rgba(0,0,0,0.15)",
  pointerEvents: "none",
};

const SC_PANEL_STYLE = {
  position: "fixed",
  top: "16px",
  right: "16px",
  zIndex: 40,
  display: "flex",
  flexDirection: "column",
  gap: "10px",
  padding: "14px 16px",
  borderRadius: "16px",
  background: "rgba(8,18,46,0.78)",
  border: "1px solid rgba(231,196,106,0.32)",
  boxShadow: "0 18px 44px rgba(0,0,0,0.45)",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  minWidth: "208px",
  fontFamily: FONT_LABEL,
};
const SC_HEADER_STYLE = { display: "flex", alignItems: "center", justifyContent: "space-between" };
const SC_LABEL_STYLE = { color: GOLD, fontSize: "11px", fontWeight: 700, letterSpacing: "3px" };
const SC_HIDE_BTN_STYLE = {
  cursor: "pointer",
  border: "none",
  background: "transparent",
  color: "#9fb0d8",
  fontSize: "14px",
  lineHeight: 1,
  padding: "2px 4px",
};
const SC_PRESETS_ROW_STYLE = { display: "flex", gap: "6px" };
const SC_PRESET_BASE = {
  cursor: "pointer",
  flex: 1,
  padding: "7px 0",
  borderRadius: "9px",
  fontSize: "13px",
  fontWeight: 700,
  fontFamily: FONT_LABEL,
  transition: "background 120ms ease",
};
const SC_PRESET_STYLE = {
  ...SC_PRESET_BASE,
  border: "1px solid rgba(231,196,106,0.3)",
  background: "rgba(255,255,255,0.06)",
  color: "#e7e9f2",
};
const SC_PRESET_ACTIVE_STYLE = {
  ...SC_PRESET_BASE,
  border: "1px solid transparent",
  background: GOLD,
  color: "#0b2a63",
};
const SC_RANGE_STYLE = { width: "100%", accentColor: GOLD, cursor: "pointer" };
const SC_VALUE_STYLE = { color: "#9fb0d8", fontSize: "12px", textAlign: "center", letterSpacing: "0.5px" };
const SC_TOGGLE_HIDDEN_STYLE = {
  position: "fixed",
  top: "16px",
  right: "16px",
  zIndex: 40,
  width: "40px",
  height: "40px",
  borderRadius: "50%",
  cursor: "pointer",
  border: "1px solid rgba(231,196,106,0.3)",
  background: "rgba(8,18,46,0.6)",
  color: GOLD,
  fontSize: "18px",
  lineHeight: 1,
};

const styles = StyleSheet.create({
  fill: { flex: 1, minHeight: "100%", width: "100%", position: "relative" },
  centered: { flex: 1, minHeight: "100%", alignItems: "center", justifyContent: "center", padding: 32 },

  eyebrow: {
    fontFamily: FONT_LABEL,
    fontSize: 12,
    letterSpacing: 5,
    fontWeight: "600",
    color: GOLD,
    textTransform: "uppercase",
    marginBottom: 10,
  },
  loadingTitle: { fontFamily: FONT_DISPLAY, fontSize: 36, fontWeight: "700", color: "#f5f1e3", letterSpacing: 1, textAlign: "center" },
  waitTitle: { fontFamily: FONT_DISPLAY, fontSize: 40, fontWeight: "700", color: "#f5f1e3", letterSpacing: 1, textAlign: "center", marginBottom: 12 },

  guardTitle: { fontFamily: FONT_DISPLAY, fontSize: 30, fontWeight: "700", color: "#f5f1e3", marginBottom: 10, textAlign: "center" },
  guardBody: { fontSize: 15, color: "#9fb0d8", textAlign: "center", maxWidth: 460, lineHeight: 22, marginTop: 8 },

  btn: { paddingVertical: 13, paddingHorizontal: 22, borderRadius: 12 },
  btnGold: { backgroundColor: GOLD },
  btnGoldText: { color: "#0b2a63", fontWeight: "800", fontSize: 15, letterSpacing: 0.3 },
});
